import MapBaseController from './map_base_controller.js'

class MapController extends MapBaseController {
  static values = {
    journeyId: String,
    interactive: Boolean,
    showEvents: Boolean
  };

  async initialize () {
    super.initialize()

    // This will create an intersection observer
    // to lazy-load journey maps, fetching journey's endpoints from Thrift (or Redis if cached)
    // and calling mapbox API only when the map element is in the viewport
    this.observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !this.map) {
        this.buildMap()
      }
    })
  }

  async connect () {
    this.observer.observe(this.element)

    // Create a listener for the journey events list clicking
    addEventListener('eventClicked', (e) => {
      this.centerOnCoordinate(e.detail.coordinates)
    })

    // iOS Safari triggers a resize event on touch scroll events
    // So we will store the window width to check if it's **really** changing later
    this.windowWidth = window.innerWidth

    // Add a listener for resizing events
    window.addEventListener('resize', () => {
      // Check if the window width has actually changed and it's not just iOS triggering a resize event on scroll
      if (window.innerWidth !== this.windowWidth) {
        // Update the window width
        this.windowWidth = window.innerWidth
        this.update()
      }
    })

    // Add a listener for fit-in-bounds events
    window.addEventListener('fitInBounds', () => {
      this.update()
    })

    // Create a listener to display journey events
    window.addEventListener('addEvents', () => {
      if (this.map && location.href.match(/\/journeys\/./)) {
        this.showEventsValue = true
        this.addEvents()
      }
    })
  }

  disconnect () {
    super.disconnect()
  }

  async buildMap () {
    this.mapPoints = await this.loadMapPoints()
    this.map = L.mapbox.map(this.element, null, {
      zoomControl: false,
      attributionControl: { compact: true }
    })
    this.map.addLayer(L.mapbox.styleLayer('mapbox://styles/mapbox/light-v10'))
    this.map.setView(this.mapPoints[0], this.zoom)
    this.addControls(this.interactiveValue)
    this.addEndPoints()
    this.addRoute()
    this.addEvents()
    this.hideLoader()
  }

  addEndPoints () {
    const start = this.mapPoints[0]
    const finish = this.mapPoints[this.mapPoints.length - 1]

    L.marker(new L.LatLng(start.lat, start.lon), {
      icon: L.divIcon({
        className: 'trip-marker trip-marker--start',
        html: '<i class="trip-marker__icon trip-marker__icon--start"></i>',
        iconSize: [14, 14]
      })
    })
      .addTo(this.map)
      .on('click', function (e) {
        e.target._map.setView(e.latlng, 22)
      })

    L.marker(new L.LatLng(finish.lat, finish.lon), {
      icon: L.divIcon({
        className: 'trip-marker trip-marker--finish',
        html: '<i class="trip-marker__icon trip-marker__icon--finish"></i>',
        iconSize: [14, 14]
      })
    })
      .addTo(this.map)
      .on('click', function (e) {
        e.target._map.setView(e.latlng, 22)
      })
  }

  addRoute () {
    this.journeyPath = L.polyline(this.getLatLng(), {
      color: '#0084AD',
      opacity: 0.4,
      weight: 4
    }).addTo(this.map)
  }

  async addEvents () {
    if (!this.showEventsValue) {
      return
    }

    this.events = await this.loadEvents()

    for (const i of this.events) {
      this.addEventMarker(i)
    }
  }

  addEventMarker (event) {
    L.marker(new L.latLng(event.lat, event.lon), {
      icon: L.divIcon({
        className: 'trip-marker trip-marker--event',
        html:
          '<i class="trip-marker__icon-event trip-marker__icon-event--' +
          event.event_title.toLowerCase().replace(' ', '-') +
          '"></i>',
        iconSize: [36, 52],
        popupAnchor: [0, -52],
        iconAnchor: [18, 52]
      })
    })
      .addTo(this.map)
      .on('click', function (e) {
        e.target._map.setView(e.latlng, 22)
      })
  }

  async loadMapPoints () {
    const response = await fetch(
      `/journeys/${this.journeyIdValue}/map_points/`
    )

    return response.json()
  }

  async loadEvents () {
    const response = await fetch(`/journeys/${this.journeyIdValue}/events/`)
    return response.json()
  }
}

export default MapController
